import { forwardRef, useImperativeHandle } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import { getEnv } from "../../environment";

// Set API endpoint
const env = getEnv();
const api_url = env.url;

const getMetrics = async (metrics) => {
  // Format the metrics data for CSV export
  // read in data and process it as needed
  try {
    // Make API call with date filter - using metrics type to determine endpoint
    const endpoint = `${api_url}/get-${metrics}-metrics/month`;
    console.log(`Fetching from endpoint: ${endpoint}`);

    const response = await axiosInstance.get(`${endpoint}`);

    console.log("Response data:", response.data);

    const lastMonthData = response.data;
    const formattedData = formatData(lastMonthData, metrics);
    return formattedData;
  } catch (error) {
    console.error(`Error fetching ${metrics} metrics:`, error);

    // More specific error messages based on axiosInstance error types
    if (error.response) {
      // Server responded with error status
      console.error(`Server error: ${error.response.status}`);
      console.error(`Error response:`, error.response.data);
      toast.error(
        `Server error: ${error.response.status} - ${error.response.statusText}`
      );
    } else if (error.request) {
      // Request was made but no response received
      console.error("No response received:", error.request);
      toast.error("No response from server. Check if the backend is running.");
    } else {
      // Something else went wrong
      console.error("Request setup error:", error.message);
      toast.error(`Failed to export ${metrics} metrics: ${error.message}`);
    }
    throw error;
  }
};

// ...existing code...

const formatData = (data, metrics) => {
  // Format the metrics data for CSV export based on type
  if (metrics === "gateway") {
    return data.map((metric) => {
      let targetUrl = null;
      let targetHostname = "";
      let targetPathname = "";
      let modelName = "";
      let cluster = "";
      let endpoint = "";

      // Handle cases where target_url might be null or malformed
      if (metric.target_url) {
        try {
          targetUrl = new URL(metric.target_url);
          targetHostname = targetUrl.hostname;
          targetPathname = targetUrl.pathname;

          // Parse the hostname to extract model name and cluster
          // Example: llmaas-nomic-embed-text-15-dev.op-epg101im1.gm.com
          const hostnameParts = targetHostname.split(".");
          if (hostnameParts.length >= 3) {
            modelName = hostnameParts[0]; // llmaas-nomic-embed-text-15-dev
            const clusterDomain = hostnameParts.slice(1).join("."); // op-epg101im1.gm.com
            // Map cluster domains to human-readable names
            switch (clusterDomain) {
              case "op-epg101im1.gm.com":
                cluster = "Dev Milford";
                break;
              case "op17wi.gm.com":
                cluster = "Prod Warren";
                break;
              case "op44im1.gm.com":
                cluster = "Prod Milford";
                break;
              default:
                cluster = clusterDomain; // Keep original domain if not recognized
                break;
            }
            endpoint = targetPathname; // /decode
          } else {
            // Fallback if hostname doesn't match expected pattern
            modelName = targetHostname;
            cluster = "";
          }
        } catch (error) {
          // Handle cases where target_url is not a valid URL
          console.warn(`Invalid target_url: ${metric.target_url}`);
          targetHostname = metric.target_url;
          modelName = "";
          cluster = "";
          endpoint = "";
        }
      }

      // Format timestamp from request_time (assuming it's in milliseconds)
      //   const timestamp = new Date(metric.request_time).toLocaleString();
      const date = new Date(metric.request_time);
      const formattedDate = date.toISOString().split("T")[0]; // YYYY-MM-DD format
      const formattedTime = date.toTimeString().split(" ")[0]; // HH:MM:SS format

      // Return formatted object matching the actual API response structure
      return {
        // timestamp: timestamp,
        request_time: formattedDate + " " + formattedTime,
        request_asms: metric.request_asms || "",
        request_client_id: metric.request_client_id || "",
        request_gmid: metric.request_gmid || "",
        request_method: metric.request_method || "",
        request_url: metric.request_url || "",
        target_url: metric.target_url || "",
        model_name: modelName,
        cluster: cluster,
        endpoint: endpoint,
        http_status: metric.http_status || "",
        http_status_message: metric.http_status_message || "",
        content_type: metric.content_type || "",
        content_length: metric.content_length || "",
      };
    });
  } else {
    // TGI metrics formatting
    return data.map((metric) => {
      const timestamp = new Date(metric.request_time).toLocaleString();

      return {
        id: metric.id,
        timestamp: timestamp,
        model_name: metric.model_name,
        pod_name: metric.pod_name,
        compute_type: metric.compute_type,
        total_time: metric.total_time,
        validation_time: metric.validation_time,
        inference_time: metric.inference_time,
        time_per_token: metric.time_per_token,
        prompt_tokens: metric.prompt_tokens,
        generated_tokens: metric.generated_tokens,
        queue_time: metric.queue_time,
        request_asms: metric.request_asms,
      };
    });
  }
};

const ExportToCSV = forwardRef(({ metrics }, ref) => {
  const exportToCSV = async () => {
    try {
      const data = await getMetrics(metrics);

      if (data.length === 0) {
        toast.error(`No ${metrics} metrics data available to export.`);
        console.warn(`No ${metrics} metrics data available to export.`);
        return;
      }

      const headers = Object.keys(data[0]).join(",");
      const csvRows = data.map((row) =>
        Object.values(row)
          .map((value) => `"${(value ?? "").toString().replace(/"/g, '""')}"`) // Handle null/undefined values and escape double quotes
          .join(",")
      );

      const csvContent = `data:text/csv;charset=utf-8,${headers}\n${csvRows.join(
        "\n"
      )}`;
      const encodedUri = encodeURI(csvContent).replace(/#/g, "%23");
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${metrics}_metrics.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${metrics} metrics exported successfully!`);
    } catch (error) {
      // Error already handled in getMetrics
    }
  };

  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  return null; // This component doesn't need to render anything to the screen
});

ExportToCSV.displayName = "ExportToCSV";

export default ExportToCSV;
